import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import { openModalAction } from '../../actions/modal';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/PageHeader/Button';
import { TextInput } from '../../components/PageHeader/TextInput';
import { HEADER_BUTTON_BLUE } from '../../constants';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import Strings from '../../Strings';
import { actions } from './redux/actions';
import { showStatus, toolTipForTable } from '../../utils';
import AdvancedFilters, { Filter } from '../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition, OrgTypeFilter } from '../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../components/AdvancedFilters/AdvancedFiltersBar';
import { getRelevantFilters } from '../../components/AdvancedFilters/helpers';
import { hasPermission, PERMISSIONS } from '../../utils/userPermissions';
import { isSuperUser } from '../../utils/userRoles';
import { EDIT_ORG_TEMPLATE_MODAL_NAME } from '../../modals/EditOrganizationTemplateModal';
import { CREATE_ORG_TEMPLATE_MODAL_NAME } from '../../modals/CreateOrganizationTemplateModal';
import { GET_ORG_TYPES_ERROR } from '../../modals/AddOrganizationModal/redux/constants';
import { actions as orgActions } from '../../modals/AddOrganizationModal/redux/actions';
import { GET_ORG_DELETION_READINESS_ERROR } from './redux/constants';
import {
  buildOrganizationTree,
  flattenOrganizationTree,
  getMatchingOrganizationIdsWithAncestors,
} from './organizationHierarchy';
import './Organization.scss';

const ORGANIZATIONS_PAGE_REQUEST = { offset: 0, limit: 0 };
const ORGANIZATION_FILTER_GROUPS = new Set([
  'org_administrative_state',
  'org_business_state',
  'org_operational_state',
  'orgType',
]);

const filterValuesByGroup = filters => getRelevantFilters(
  (filters || []).filter(filter => ORGANIZATION_FILTER_GROUPS.has(filter.group)),
).reduce((valuesByGroup, filter) => {
  return {
    ...valuesByGroup,
    [filter.group]: [...(valuesByGroup[filter.group] || []), filter.value],
  };
}, {});

const filterValueAliases = {
  ProofOfService: 'Proof-of-Service',
};

const matchesOrganizationFilters = (organization, valuesByGroup) => {
  const valueForGroup = {
    org_administrative_state: organization.state?.administrative_state,
    org_business_state: organization.state?.business_state,
    org_operational_state: organization.state?.operational_state,
    orgType: organization.organization_type,
  };

  return Object.entries(valuesByGroup).every(([group, values]) => {
    const organizationValue = valueForGroup[group];
    return values.some(value => (filterValueAliases[value] || value) === organizationValue);
  });
};

export const getOrgUrlToNavigate = (orgId, orgName) => {
  if (orgId && orgName) {
    const baseUrl = `/admin/organizations/${encodeURIComponent(orgId)}/${orgName}`;
    if (hasPermission(PERMISSIONS.ORGANIZATION_MANAGEMENT) || isSuperUser()) {
      return `${baseUrl}/config`;
    }
    return `${baseUrl}/state-history`;
  }
};

const renderCareProviderOrganizations = organizations => {
  if (!organizations || organizations.length === 0) {
    return '-';
  }

  if (organizations.length === 1) {
    return organizations[0];
  }

  const [firstOrganization, ...remainingOrganizations] = organizations;
  return toolTipForTable(`${firstOrganization}...`, remainingOrganizations);
};

function OrganizationNameCell(info) {
  const { row: { original: organization } } = info;
  const { isHierarchyExpanded, onToggleHierarchy } = organization;
  const toggleLabel = Strings.formatString(
    isHierarchyExpanded ? Strings.collapseOrganization : Strings.expandOrganization,
    organization.name,
  );

  return (
    <div
      className="organization-hierarchy-name"
      style={{ paddingLeft: `${organization.hierarchyDepth * 36}px` }}
    >
      {organization.hasHierarchyChildren ? (
        <button
          type="button"
          className={`table-row-expander ${isHierarchyExpanded ? 'expanded' : ''}`}
          onClick={event => {
            event.stopPropagation();
            onToggleHierarchy(organization.masked_id);
          }}
          aria-label={toggleLabel}
          aria-expanded={isHierarchyExpanded}
          data-testid={`organization-hierarchy-toggle-${organization.masked_id}`}
        />
      ) : (
        <span className="organization-hierarchy-toggle-placeholder" />
      )}
      <span>{organization.name}</span>
    </div>
  );
}

OrganizationNameCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      masked_id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      hierarchyDepth: PropTypes.number.isRequired,
      hasHierarchyChildren: PropTypes.bool.isRequired,
      isHierarchyExpanded: PropTypes.bool.isRequired,
      onToggleHierarchy: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
};

function Organization(props) {
  const pageRequest = React.useRef(ORGANIZATIONS_PAGE_REQUEST);
  const { isLoading, organizations, filters } = props;
  const {
    getOrganizations,
    getOrgDeletionReadiness,
    getOrgTypes,
    onAddOrganization,
    onEditOrganization,
    onNavigate,
    openConfirmModal,
  } = props;

  const [orgTypes, setOrgTypes] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [collapsedOrganizationIds, setCollapsedOrganizationIds] = React.useState(new Set());

  const canManageOrganizations = hasPermission(PERMISSIONS.ORGANIZATION_MANAGEMENT) || isSuperUser();

  React.useEffect(() => {
    getOrganizations(pageRequest.current);
    getOrgTypes().then(resp => {
      if (resp?.type !== GET_ORG_TYPES_ERROR) {
        setOrgTypes(resp?.response || []);
      }
    });
  }, []);

  const hierarchyTree = React.useMemo(() => buildOrganizationTree(organizations), [organizations]);
  const activeFilterValues = React.useMemo(() => filterValuesByGroup(filters), [filters]);
  const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase();
  const hierarchyFilteringActive = normalizedSearchQuery.length > 0 || Object.keys(activeFilterValues).length > 0;
  const includedOrganizationIds = React.useMemo(() => {
    if (!hierarchyFilteringActive) {
      return undefined;
    }

    return getMatchingOrganizationIdsWithAncestors(organizations, organization => {
      const matchesSearch = !normalizedSearchQuery
        || (organization.name || '').toLocaleLowerCase().includes(normalizedSearchQuery);
      return matchesSearch && matchesOrganizationFilters(organization, activeFilterValues);
    });
  }, [activeFilterValues, hierarchyFilteringActive, normalizedSearchQuery, organizations]);
  const visibleOrganizations = React.useMemo(() => flattenOrganizationTree(hierarchyTree, {
    collapsedIds: collapsedOrganizationIds,
    includedIds: includedOrganizationIds,
    ignoreCollapsed: hierarchyFilteringActive,
  }), [collapsedOrganizationIds, hierarchyFilteringActive, hierarchyTree, includedOrganizationIds]);

  const toggleOrganization = React.useCallback(organizationId => {
    setCollapsedOrganizationIds(current => {
      const next = new Set(current);
      if (next.has(organizationId)) {
        next.delete(organizationId);
      } else {
        next.add(organizationId);
      }
      return next;
    });
  }, []);
  const tableOrganizations = React.useMemo(() => visibleOrganizations.map(organization => ({
    ...organization,
    isHierarchyExpanded: hierarchyFilteringActive || !collapsedOrganizationIds.has(organization.masked_id),
    onToggleHierarchy: toggleOrganization,
  })), [collapsedOrganizationIds, hierarchyFilteringActive, toggleOrganization, visibleOrganizations]);

  const getHeaderComponents = () => {
    return (
      <>
        <TextInput 
          class="search" 
          placeholder={Strings.search} 
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <AdvancedFilters>
          <Filter definition={getFiltersDefinition().org_administrative_state} />
          <Filter definition={getFiltersDefinition().org_business_state} />
          <Filter definition={getFiltersDefinition().org_operational_state} />
          <Filter definition={OrgTypeFilter(orgTypes)?.orgType} />
        </AdvancedFilters>
        {canManageOrganizations && (
          <Button
            class={HEADER_BUTTON_BLUE}
            onClick={() => onAddOrganization(pageRequest.current)}
            title={Strings.addOrganization}
          />
        )}
      </>
    );
  };

  const onRowClick = row => {
    const url = getOrgUrlToNavigate(row.masked_id, row.name);
    onNavigate(url);
  };

  const onOrgEdit = (...rowActionArguments) => {
    const organization = rowActionArguments[1];
    onEditOrganization(organization, pageRequest.current);
  };

  const renderDeletionReadiness = readiness => {
    if (readiness?.can_delete) {
      return (
        <div>
          {Strings.deleteOrgNoBlockers}
        </div>
      );
    }

    return (
      <div>
        <div>{Strings.deleteOrgBlockedMessage}</div>
        <ul>
          {(readiness?.blockers || []).map(blocker => (
            <li key={blocker.code}>
              {blocker.message}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderDeletionReadinessError = response => {
    const message =
      response?.response?.data?.standard_error?.message ||
      response?.response?.data?.error?.message ||
      Strings.deleteOrgReadinessFailed;

    return (
      <div>
        {message}
      </div>
    );
  };

  const onOrgDelete = (...rowActionArguments) => {
    const organization = rowActionArguments[1];
    getOrgDeletionReadiness(organization.masked_id).then(response => {
      const hasError = response?.type === GET_ORG_DELETION_READINESS_ERROR;
      const readiness = response?.response?.data || response?.response;
      const hasBlockers = hasError || !readiness?.can_delete || (readiness?.blockers || []).length > 0;
      const hasHardBlockers = (readiness?.blockers || []).some(blocker => blocker.code === 'child_organizations');
      const canForceClean = hasBlockers && !hasError && isSuperUser() && !hasHardBlockers;
      const title = hasBlockers && !canForceClean ? (
        <span>
          <b>{organization.name}</b>: {Strings.deleteOrgBlockedCaption}
          {hasError ? renderDeletionReadinessError(response) : renderDeletionReadiness(readiness)}
        </span>
      ) : (
        <span>
          {Strings.deleteOrgWarning} <b>{organization.name}</b>?
          {renderDeletionReadiness(readiness)}
          {canForceClean && <div>{Strings.deleteOrgForceCleanWarning}</div>}
        </span>
      );
      const data = {
        title,
        onConfirmAction: actions.deleteOrg(organization.masked_id, canForceClean),
        onCancelAction: null,
        confirmPostAction: actions.getOrganizations(pageRequest.current),
        hideCaution: hasBlockers && !canForceClean,
        hideYesButton: hasBlockers && !canForceClean,
        noButtonText: hasBlockers && !canForceClean ? Strings.ok : undefined,
        requiredConfirmationText: canForceClean ? organization.name : undefined,
        requiredConfirmationLabel: canForceClean ? Strings.deleteOrgForceCleanConfirmationLabel : undefined,
        requiredConfirmationMessage: canForceClean ? `${Strings.deleteOrgForceCleanConfirmationMessage} (${organization.name})` : undefined,
      };
      openConfirmModal(data);
    });
  };

  const launchOrg = (...rowActionArguments) => {
    const organization = rowActionArguments[1];
    const data = {
      caption: `${Strings.launch} ${organization.name}`,
      title: (
        <span>
          {Strings.launchOrgWarning} <b>{organization.name}</b>?
        </span>
      ),

      onConfirmAction: actions.launchOrganization(organization.masked_id),
      onCancelAction: null,
      confirmPostAction: actions.getOrganizations(pageRequest.current),
      hideCaution: true,
    };
    openConfirmModal(data);
  };

  const unsuspendOrg = (...rowActionArguments) => {
    const organization = rowActionArguments[1];
    const data = {
      caption: `${Strings.unsuspend} ${organization.name}`,
      title: (
        <span>
          {Strings.unsuspendOrgWarning}?
        </span>
      ),
      onConfirmAction: actions.unsuspendOrganization(organization.masked_id, {}),
      confirmPostAction: actions.getOrganizations(pageRequest.current),
      onCancelAction: null,
      hideCaution: true,
    };
    openConfirmModal(data);
  };

  const buttons = [
    canManageOrganizations && {
      icon: 'edit',
      onClick: onOrgEdit,
      text: Strings.edit,
    },
    {
      icon: 'delete',
      onClick: onOrgDelete,
      text: Strings.delete,
    },
    {
      icon: 'launch',
      onClick: launchOrg,
      disabled: (...rowActionArguments) => {
        const organization = rowActionArguments[1];
        return !(organization.state.administrative_state !== 'New' && organization.state.administrative_state !== 'Deleted' && organization.state.business_state === 'Proof-of-Service');
      },
      text: Strings.launch,
    },
    {
      icon: 'refresh',
      onClick: unsuspendOrg,
      disabled: (...rowActionArguments) => {
        const organization = rowActionArguments[1];
        return organization.state.administrative_state !== 'Suspended';
      },
      text: Strings.unsuspend,
    }
  ].filter(Boolean);

  const columns = [
    columnHelper.accessor('name', {
      header: Strings.name,
      enableSorting: false,
      cell: OrganizationNameCell,
    }),
    ...(isSuperUser()
      ? [
          columnHelper.accessor('organization_type', {
            header: Strings.organizationType,
            cell: info => info.getValue() || '-',
          }),
        ]
      : []),
    columnHelper.accessor('organization_managers', {
      header: Strings.organizationManagers,
    }),
    columnHelper.accessor('cap_managers', {
      header: Strings.capManagers,
    }),
    columnHelper.accessor('admins', {
      header: Strings.admin,
    }),
    columnHelper.accessor('patients', {
      header: Strings.patients,
    }),
    columnHelper.accessor('care_provided_organizations_count', {
      header: Strings.careProviderForOrganizations,
      cell: info => {
        const count = info.getValue() || 0;
        const names = info.row.original?.care_provided_organizations || [];
        return names.length > 0 ? toolTipForTable(count, names) : count;
      },
    }),
    columnHelper.accessor('care_provider_organizations', {
      header: Strings.careProviders,
      cell: info => renderCareProviderOrganizations(info.getValue()),
      enableSorting: false,
    }),
    columnHelper.accessor('two_fa_enabled', {
      header: Strings.twoFAEnabled,
      cell: info => showStatus(info.getValue() ? 'enabled' : 'disabled'),
    }),
    columnHelper.accessor('administrative_state', {
      header: Strings.administrativeState,
      accessorFn: row => row.state?.administrative_state,
    }),
    columnHelper.accessor('business_state', {
      header: Strings.businessState,
      accessorFn: row => row.state?.business_state,
    }),
    columnHelper.accessor('operational_state', {
      header: Strings.operationalState,
      accessorFn: row => row.state?.operational_state,
    }),
  ];

  return (
    <>
      <PageHeader
        right={getHeaderComponents()}
      />
      <AdvancedFiltersBar customFilters={OrgTypeFilter(orgTypes)} />
      <Table3
        isLoading={isLoading}
        name="organizations"
        data={tableOrganizations}
        columns={columns}
        onRowClick={onRowClick}
        enableColumnFiltering
        pagination={{
          offset: 0,
          limit: Math.max(tableOrganizations.length, 1),
          totalRecords: tableOrganizations.length,
        }}
        noPagination
        buttons={buttons}
      />
    </>
  );
}

Organization.propTypes = {
  isLoading: PropTypes.bool,
  organizations: PropTypes.arrayOf(PropTypes.shape({
    masked_id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    parent_org_id: PropTypes.string,
  })),
  filters: PropTypes.arrayOf(PropTypes.shape({
    group: PropTypes.string,
    value: PropTypes.string,
    state: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
  })),
  getOrganizations: PropTypes.func.isRequired,
  getOrgDeletionReadiness: PropTypes.func.isRequired,
  getOrgTypes: PropTypes.func.isRequired,
  onAddOrganization: PropTypes.func.isRequired,
  onEditOrganization: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  const { organizations } = state.superUser;
  const orgTemplates = state.superUser.organizations;

  return {
    organizations: organizations && organizations?.organization,
    isLoading: organizations && organizations?.isLoading,
    filters: state.entities.advancedFilters.filters.items,
    templates: orgTemplates?.templates,
    templatesLoading: orgTemplates?.templatesLoading,
  };
};

const mapDispatchToProps = dispatch => ({
  onNavigate: path => dispatch(push(path)),
  getOrganizations: pageRequest => dispatch(actions.getOrganizations(pageRequest)),
  getOrgDeletionReadiness: id => dispatch(actions.getOrgDeletionReadiness(id)),
  getOrgTemplates: pageRequest => dispatch(actions.getOrgTemplates(pageRequest)),
  getOrgTypes: () => dispatch(orgActions.getOrgTypes()),
  onAddOrganization: pageRequest => dispatch(openModalAction('edit-organization', { pageRequest })),
  onEditOrganization: (organization, pageRequest) =>
    dispatch(
      openModalAction('edit-organization', {
        organization,
        pageRequest,
      }),
    ),
  openEditTemplateModal: template =>
    dispatch(
      openModalAction(EDIT_ORG_TEMPLATE_MODAL_NAME, {
        template,
      }),
    ),
  openCreateTemplateModal: () => dispatch(openModalAction(CREATE_ORG_TEMPLATE_MODAL_NAME)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  launchOrganization: id => dispatch(actions.launchOrganization(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Organization);
